import { useEffect, useState, useRef, useCallback, useMemo } from 'react';

type LogEntry = {
  id: string;
  type: 'log' | 'info' | 'warn' | 'error';
  message: string;
  timestamp: Date;
  stack?: string;
};

type NetworkRequest = {
  id: string;
  timestamp: string;
  method: string;
  url: string;
  status?: number;
  duration?: number;
  error?: string;
  requestHeaders?: Record<string, string>;
  requestBody?: any;
  responseHeaders?: Record<string, string>;
  responseBody?: any;
  stack?: string;
};

type NetworkFilter = 'all' | 'success' | 'error' | 'pending';
type TabType = 'console' | 'network' | 'storage';

const MAX_LOGS = 400;
const MAX_NETWORK_REQUESTS = 200;
const LOG_LEVELS: LogEntry['type'][] = ['log', 'info', 'warn', 'error'];
const DEV_CONSOLE_STORAGE_KEY = 'dev_console_enabled';
const NETWORK_REQUESTS_KEY = 'dev_console_network_requests';
const LOGS_STORAGE_KEY = 'dev_console_logs';

const deserializeLogs = (raw: string | null): LogEntry[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as Array<Omit<LogEntry, 'timestamp'> & { timestamp: string }>;
    return parsed.map((entry) => ({
      ...entry,
      timestamp: new Date(entry.timestamp),
    }));
  } catch {
    return [];
  }
};

const serializeLogs = (entries: LogEntry[]) =>
  JSON.stringify(entries.map((entry) => ({ ...entry, timestamp: entry.timestamp.toISOString() })));

type DevConsoleProps = {
  forceEnabled?: boolean;
  forceVisible?: boolean;
  floatingOffsetPx?: number;
};

export default function DevConsole({
  forceEnabled = false,
  forceVisible = false,
  floatingOffsetPx = 12,
}: DevConsoleProps) {
  const envEnabled = import.meta.env.DEV || import.meta.env.VITE_DEV_CONSOLE_ENABLED === 'true';
  const [isConsoleEnabled, setIsConsoleEnabled] = useState(() => {
    if (forceEnabled) {
      return true;
    }
    if (typeof window === 'undefined') {
      return envEnabled;
    }
    const stored = window.localStorage.getItem(DEV_CONSOLE_STORAGE_KEY);
    if (stored === null) {
      return envEnabled;
    }
    return stored === 'true';
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('console');
  const [isCapturing, setIsCapturing] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const [isGrouped, setIsGrouped] = useState(true);
  const [isCompactLogs, setIsCompactLogs] = useState(true);
  const [isWrapLines, setIsWrapLines] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilters, setLevelFilters] = useState<LogEntry['type'][]>(LOG_LEVELS);
  const [logs, setLogs] = useState<LogEntry[]>(() => {
    if (typeof window === 'undefined') {
      return [];
    }
    return deserializeLogs(window.sessionStorage.getItem(LOGS_STORAGE_KEY));
  });
  const [networkRequests, setNetworkRequests] = useState<NetworkRequest[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = window.sessionStorage.getItem(NETWORK_REQUESTS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [networkFilter, setNetworkFilter] = useState<NetworkFilter>('all');
  const [networkSearchQuery, setNetworkSearchQuery] = useState('');
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [storageSearchQuery, setStorageSearchQuery] = useState('');
  const [selectedStorageItem, setSelectedStorageItem] = useState<{
    scope: 'localStorage' | 'sessionStorage' | 'cookies';
    key: string;
    value: string;
  } | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const pendingLogsRef = useRef<LogEntry[]>([]);
  const isPausedRef = useRef(isPaused);
  const originalConsole = useRef({
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
  });
  const originalFetch = useRef<typeof fetch>();

  const appendLogs = useCallback((newEntries: LogEntry[]) => {
    setLogs((prev) => {
      const next = [...prev, ...newEntries];
      if (next.length > MAX_LOGS) {
        return next.slice(-MAX_LOGS);
      }
      return next;
    });
  }, []);

  const addNetworkRequest = useCallback((request: NetworkRequest) => {
    setNetworkRequests((prev) => {
      const next = [...prev, request];
      if (next.length > MAX_NETWORK_REQUESTS) {
        return next.slice(-MAX_NETWORK_REQUESTS);
      }
      // Also persist to sessionStorage
      try {
        sessionStorage.setItem(NETWORK_REQUESTS_KEY, JSON.stringify(next));
      } catch (e) {
        // Ignore storage errors
      }
      return next;
    });
  }, []);

  const updateNetworkRequest = useCallback((id: string, updates: Partial<NetworkRequest>) => {
    setNetworkRequests((prev) => {
      const next = prev.map(req => req.id === id ? { ...req, ...updates } : req);
      try {
        sessionStorage.setItem(NETWORK_REQUESTS_KEY, JSON.stringify(next));
      } catch (e) {
        // Ignore storage errors
      }
      return next;
    });
  }, []);

  // Load network requests from sessionStorage
  const refreshNetwork = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(NETWORK_REQUESTS_KEY);
      if (stored) {
        setNetworkRequests(JSON.parse(stored));
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'network') {
      refreshNetwork();
      const interval = setInterval(refreshNetwork, 1000);
      return () => clearInterval(interval);
    }
  }, [activeTab, refreshNetwork]);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    if (forceEnabled) {
      setIsConsoleEnabled(true);
    }
  }, [forceEnabled]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DEV_CONSOLE_STORAGE_KEY, String(isConsoleEnabled));
    }
  }, [isConsoleEnabled]);

  useEffect(() => {
    if (!isConsoleEnabled) {
      setIsCapturing(false);
    }
  }, [isConsoleEnabled]);

  // Auto-enable console when opened so the network interceptor starts capturing
  useEffect(() => {
    if (isOpen && !isConsoleEnabled) {
      setIsConsoleEnabled(true);
      setIsCapturing(true);
    }
  }, [isOpen, isConsoleEnabled]);

  useEffect(() => {
    // Keyboard shortcut: Ctrl+Shift+K
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'K') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleToggle = () => setIsOpen((prev) => !prev);
    window.addEventListener('room:dev-console-toggle', handleToggle);
    return () => window.removeEventListener('room:dev-console-toggle', handleToggle);
  }, []);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('room:dev-console-state', {
        detail: { open: isOpen },
      })
    );
  }, [isOpen]);

  // Intercept fetch for network monitoring
  useEffect(() => {
    if (!isConsoleEnabled) return;

    if (!originalFetch.current) {
      originalFetch.current = window.fetch;
    }

    window.fetch = async (...args) => {
      const [resource, config] = args;
      const url = typeof resource === 'string' ? resource : resource.url;
      const method = config?.method || 'GET';
      const startTime = Date.now();
      const requestId = `${Date.now()}-${Math.random()}`;

      // Capture request details
      const requestHeaders: Record<string, string> = {};
      if (config?.headers) {
        if (config.headers instanceof Headers) {
          config.headers.forEach((value, key) => {
            requestHeaders[key] = value;
          });
        } else if (typeof config.headers === 'object') {
          Object.assign(requestHeaders, config.headers);
        }
      }

      let requestBody: any = undefined;
      try {
        if (config?.body) {
          if (typeof config.body === 'string') {
            requestBody = JSON.parse(config.body);
          } else {
            requestBody = config.body.toString();
          }
        }
      } catch {
        requestBody = config?.body?.toString() || undefined;
      }

      // Add request to network log
      addNetworkRequest({
        id: requestId,
        timestamp: new Date().toISOString(),
        method,
        url,
        requestHeaders,
        requestBody,
      });

      try {
        const response = await originalFetch.current!.call(window, ...args);
        const duration = Date.now() - startTime;

        // Capture response details
        const responseHeaders: Record<string, string> = {};
        response.headers.forEach((value, key) => {
          responseHeaders[key] = value;
        });

        // Clone response to read body
        const clonedResponse = response.clone();
        let responseBody: any = undefined;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            responseBody = await clonedResponse.json();
          } else {
            responseBody = await clonedResponse.text();
          }
        } catch {
          // Ignore body read errors
        }

        updateNetworkRequest(requestId, {
          status: response.status,
          duration,
          responseHeaders,
          responseBody,
        });

        return response;
      } catch (error: any) {
        const duration = Date.now() - startTime;
        updateNetworkRequest(requestId, {
          error: error?.message || 'Network error',
          duration,
          stack: error?.stack,
        });
        throw error;
      }
    };

    return () => {
      if (originalFetch.current) {
        window.fetch = originalFetch.current;
      }
    };
  }, [isConsoleEnabled, addNetworkRequest, updateNetworkRequest]);

  useEffect(() => {
    if (!isConsoleEnabled || !isCapturing) return;

    const addLog = (type: LogEntry['type'], args: any[]) => {
      const message = args
        .map((arg) => {
          if (typeof arg === 'object') {
            try {
              return isCompactLogs ? JSON.stringify(arg) : JSON.stringify(arg, null, 2);
            } catch {
              return String(arg);
            }
          }
          return String(arg);
        })
        .join(' ');

      const entry: LogEntry = {
        id: `${Date.now()}-${Math.random()}`,
        type,
        message,
        timestamp: new Date(),
      };

      if (type === 'error' && args[0] instanceof Error) {
        entry.stack = args[0].stack;
      }

      if (isPausedRef.current) {
        pendingLogsRef.current.push(entry);
      } else {
        appendLogs([entry]);
      }
    };

    // Patch console methods
    console.log = (...args) => {
      originalConsole.current.log(...args);
      addLog('log', args);
    };

    console.info = (...args) => {
      originalConsole.current.info(...args);
      addLog('info', args);
    };

    console.warn = (...args) => {
      originalConsole.current.warn(...args);
      addLog('warn', args);
    };

    console.error = (...args) => {
      originalConsole.current.error(...args);
      addLog('error', args);
    };

    // Capture window errors
    const handleError = (event: ErrorEvent) => {
      addLog('error', [event.error || event.message]);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      addLog('error', [`Unhandled Promise Rejection: ${event.reason}`]);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      // Restore original console methods
      console.log = originalConsole.current.log;
      console.info = originalConsole.current.info;
      console.warn = originalConsole.current.warn;
      console.error = originalConsole.current.error;
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [appendLogs, isCapturing, isConsoleEnabled, isCompactLogs]);

  useEffect(() => {
    if (isOpen && isAutoScroll && !isPaused && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isAutoScroll, isOpen, isPaused, logs]);

  useEffect(() => {
    if (!isPaused && pendingLogsRef.current.length > 0) {
      appendLogs(pendingLogsRef.current);
      pendingLogsRef.current = [];
    }
  }, [appendLogs, isPaused]);

  useEffect(() => {
    try {
      sessionStorage.setItem(LOGS_STORAGE_KEY, serializeLogs(logs));
    } catch {
      // Ignore storage errors
    }
  }, [logs]);

  const clearLogs = () => {
    setLogs([]);
    pendingLogsRef.current = [];
    sessionStorage.removeItem(LOGS_STORAGE_KEY);
  };

  const clearNetwork = () => {
    sessionStorage.removeItem(NETWORK_REQUESTS_KEY);
    setNetworkRequests([]);
  };

  const clearAllData = () => {
    if (confirm('Clear all data? This will clear cookies, localStorage, sessionStorage, and reload the page.')) {
      // Clear localStorage
      localStorage.clear();
      // Clear sessionStorage
      sessionStorage.clear();
      // Clear cookies
      document.cookie.split(";").forEach((c) => {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      // Reload
      window.location.reload();
    }
  };

  const clearCacheAndReload = async () => {
    if (confirm('Clear cache & reload? This will:\n• Clear localStorage & sessionStorage\n• Unregister service workers\n• Reload the page\n\nAfter this, auto-update detection will work properly.')) {
      try {
        // Clear storage
        localStorage.clear();
        sessionStorage.clear();

        // Unregister all service workers
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(reg => reg.unregister()));
        }

        // Reload (hard reload to bypass cache)
        window.location.reload();
      } catch (error) {
        console.error('Error clearing cache:', error);
        // Reload anyway
        window.location.reload();
      }
    }
  };

  const getLogColor = (type: LogEntry['type']) => {
    switch (type) {
      case 'error':
        return '#ef4444';
      case 'warn':
        return '#f59e0b';
      case 'info':
        return '#3b82f6';
      default:
        return '#e2e8f0';
    }
  };

  const getLogIcon = (type: LogEntry['type']) => {
    switch (type) {
      case 'error':
        return '❌';
      case 'warn':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return '▶';
    }
  };

  const getStatusColor = (status?: number) => {
    if (!status) return '#94a3b8';
    if (status >= 200 && status < 300) return '#10b981';
    if (status >= 300 && status < 400) return '#3b82f6';
    if (status >= 400 && status < 500) return '#f59e0b';
    return '#ef4444';
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET':
        return '#10b981';
      case 'POST':
        return '#3b82f6';
      case 'PUT':
        return '#f59e0b';
      case 'DELETE':
        return '#ef4444';
      case 'PATCH':
        return '#8b5cf6';
      default:
        return '#94a3b8';
    }
  };

  const filteredLogs = useMemo(() => {
    const activeLevels = new Set(levelFilters);
    const query = searchQuery.trim().toLowerCase();

    return logs.filter((log) => {
      if (!activeLevels.has(log.type)) return false;
      if (!query) return true;
      const content = [log.message, log.stack].filter(Boolean).join(' ').toLowerCase();
      return content.includes(query);
    });
  }, [levelFilters, logs, searchQuery]);

  const filteredNetworkRequests = useMemo(() => {
    const query = networkSearchQuery.trim().toLowerCase();

    return networkRequests.filter((req) => {
      // Filter by status
      if (networkFilter === 'success' && (!req.status || req.status >= 400 || req.error)) return false;
      if (networkFilter === 'error' && (!req.error && req.status && req.status < 400)) return false;
      if (networkFilter === 'pending' && (req.status || req.error)) return false;

      // Filter by search query
      if (!query) return true;
      const searchableContent = [
        req.method,
        req.url,
        req.status?.toString(),
        req.error,
      ].filter(Boolean).join(' ').toLowerCase();
      return searchableContent.includes(query);
    });
  }, [networkRequests, networkFilter, networkSearchQuery]);

  const displayLogs = useMemo(() => {
    if (!isGrouped) {
      return filteredLogs.map((log) => ({ ...log, count: 1 }));
    }

    return filteredLogs.reduce<Array<LogEntry & { count: number }>>((acc, log) => {
      const last = acc[acc.length - 1];
      if (last && last.type === log.type && last.message === log.message && last.stack === log.stack) {
        last.count += 1;
        return acc;
      }
      acc.push({ ...log, count: 1 });
      return acc;
    }, []);
  }, [filteredLogs, isGrouped]);

  const networkStats = useMemo(() => {
    const total = networkRequests.length;
    const success = networkRequests.filter(r => r.status && r.status >= 200 && r.status < 400 && !r.error).length;
    const errors = networkRequests.filter(r => r.error || (r.status && r.status >= 400)).length;
    const pending = networkRequests.filter(r => !r.status && !r.error).length;
    return { total, success, errors, pending };
  }, [networkRequests]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'console':
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#0f172a', color: '#e2e8f0', minHeight: 0 }}>
            <div style={{ padding: '4px 6px', background: '#1e293b', display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '9px', cursor: 'pointer', color: '#e2e8f0' }}>
                  <input
                    type="checkbox"
                    checked={isConsoleEnabled}
                    onChange={(e) => setIsConsoleEnabled(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Enable
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '9px', cursor: 'pointer', color: '#e2e8f0' }}>
                  <input
                    type="checkbox"
                    checked={isCapturing}
                    onChange={(e) => setIsCapturing(e.target.checked)}
                    disabled={!isConsoleEnabled}
                    style={{ cursor: 'pointer' }}
                  />
                  Capture
                </label>
                <button onClick={clearLogs} style={{ padding: '2px 6px', borderRadius: '2px', border: '1px solid #334155', background: '#0f172a', color: '#e2e8f0', cursor: 'pointer', fontSize: '9px' }}>
                  Clear
                </button>
                <span style={{ fontSize: '9px', color: '#94a3b8' }}>
                  {filteredLogs.length}/{logs.length} • Max {MAX_LOGS}
                </span>
                {LOG_LEVELS.map((level) => {
                  const isActive = levelFilters.includes(level);
                  return (
                    <button
                      key={level}
                      onClick={() => {
                        setLevelFilters((prev) => (
                          prev.includes(level) ? prev.filter((item) => item !== level) : [...prev, level]
                        ));
                      }}
                      style={{
                        padding: '2px 5px',
                        borderRadius: '2px',
                        border: `1px solid ${isActive ? getLogColor(level) : '#334155'}`,
                        background: isActive ? '#0f172a' : 'transparent',
                        color: isActive ? getLogColor(level) : '#94a3b8',
                        fontSize: '8px',
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                      }}
                    >
                      {level}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  style={{
                    flex: '1 1 120px',
                    maxWidth: '200px',
                    minWidth: '100px',
                    padding: '2px 5px',
                    borderRadius: '2px',
                    border: '1px solid #334155',
                    background: '#0f172a',
                    color: '#e2e8f0',
                    fontSize: '9px',
                  }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '9px', color: '#94a3b8' }}>
                  <input type="checkbox" checked={isGrouped} onChange={(e) => setIsGrouped(e.target.checked)} />
                  Group
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '9px', color: '#94a3b8' }}>
                  <input type="checkbox" checked={isAutoScroll} onChange={(e) => setIsAutoScroll(e.target.checked)} />
                  Auto
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '9px', color: '#94a3b8' }}>
                  <input type="checkbox" checked={isCompactLogs} onChange={(e) => setIsCompactLogs(e.target.checked)} />
                  Min
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '9px', color: '#94a3b8' }}>
                  <input type="checkbox" checked={isWrapLines} onChange={(e) => setIsWrapLines(e.target.checked)} />
                  Wrap
                </label>
                <button
                  onClick={() => setIsPaused((prev) => !prev)}
                  style={{
                    padding: '2px 6px',
                    borderRadius: '2px',
                    border: '1px solid #334155',
                    background: isPaused ? '#ef4444' : '#0f172a',
                    color: '#e2e8f0',
                    fontSize: '9px',
                    cursor: 'pointer',
                  }}
                >
                  {isPaused ? 'Resume' : 'Pause'}
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 6px', fontFamily: 'monospace', fontSize: '10px' }}>
              {!isConsoleEnabled && (
                <div style={{ color: '#f59e0b', textAlign: 'center', padding: '6px', border: '1px solid #334155', borderRadius: '3px', marginBottom: '4px', background: '#1e293b', fontSize: '9px' }}>
                  DevConsole capture is disabled by environment/feature flag.
                </div>
              )}
              {displayLogs.length === 0 ? (
                <div style={{ color: '#64748b', textAlign: 'center', padding: '12px', fontSize: '9px' }}>
                  No console output yet. {!isCapturing && '(Capturing is paused)'}
                </div>
              ) : (
                displayLogs.map((log) => (
                  <div key={log.id} style={{ marginBottom: '3px', paddingBottom: '2px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                      <span style={{ fontSize: '10px' }}>{getLogIcon(log.type)}</span>
                      <span style={{ fontSize: '8px', color: '#94a3b8' }}>
                        {log.timestamp.toLocaleTimeString()}
                      </span>
                      <span style={{ fontSize: '8px', fontWeight: '600', color: getLogColor(log.type), textTransform: 'uppercase' }}>
                        {log.type}
                      </span>
                      {log.count && log.count > 1 && (
                        <span style={{ fontSize: '8px', color: '#94a3b8', background: '#1e293b', padding: '1px 4px', borderRadius: '6px' }}>
                          x{log.count}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        paddingLeft: '16px',
                        whiteSpace: isWrapLines ? 'pre-wrap' : 'pre',
                        wordBreak: isWrapLines ? 'break-word' : 'normal',
                        fontSize: '9px',
                      }}
                    >
                      {log.message}
                    </div>
                    {log.stack && (
                      <div
                        style={{
                          paddingLeft: '16px',
                          marginTop: '2px',
                          fontSize: '8px',
                          color: '#94a3b8',
                          whiteSpace: isWrapLines ? 'pre-wrap' : 'pre',
                        }}
                      >
                        {log.stack}
                      </div>
                    )}
                  </div>
                ))
              )}
              <div ref={logsEndRef} />
            </div>
          </div>
        );

      case 'network':
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#0f172a', color: '#e2e8f0', minHeight: 0 }}>
            {/* Network filters and stats */}
            <div style={{ padding: '4px 6px', background: '#1e293b', display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center' }}>
                <button
                  onClick={() => setNetworkFilter('all')}
                  style={{
                    padding: '2px 6px',
                    borderRadius: '2px',
                    border: `1px solid ${networkFilter === 'all' ? '#3b82f6' : '#334155'}`,
                    background: networkFilter === 'all' ? '#1e40af' : 'transparent',
                    color: networkFilter === 'all' ? '#e2e8f0' : '#94a3b8',
                    fontSize: '8px',
                    cursor: 'pointer',
                  }}
                >
                  All ({networkStats.total})
                </button>
                <button
                  onClick={() => setNetworkFilter('success')}
                  style={{
                    padding: '2px 6px',
                    borderRadius: '2px',
                    border: `1px solid ${networkFilter === 'success' ? '#10b981' : '#334155'}`,
                    background: networkFilter === 'success' ? '#065f46' : 'transparent',
                    color: networkFilter === 'success' ? '#e2e8f0' : '#94a3b8',
                    fontSize: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Success ({networkStats.success})
                </button>
                <button
                  onClick={() => setNetworkFilter('error')}
                  style={{
                    padding: '2px 6px',
                    borderRadius: '2px',
                    border: `1px solid ${networkFilter === 'error' ? '#ef4444' : '#334155'}`,
                    background: networkFilter === 'error' ? '#7f1d1d' : 'transparent',
                    color: networkFilter === 'error' ? '#e2e8f0' : '#94a3b8',
                    fontSize: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Errors ({networkStats.errors})
                </button>
                <button
                  onClick={() => setNetworkFilter('pending')}
                  style={{
                    padding: '2px 6px',
                    borderRadius: '2px',
                    border: `1px solid ${networkFilter === 'pending' ? '#f59e0b' : '#334155'}`,
                    background: networkFilter === 'pending' ? '#78350f' : 'transparent',
                    color: networkFilter === 'pending' ? '#e2e8f0' : '#94a3b8',
                    fontSize: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Pending ({networkStats.pending})
                </button>
                <button onClick={clearNetwork} style={{ padding: '2px 6px', borderRadius: '2px', border: '1px solid #334155', background: '#1e293b', color: '#e2e8f0', cursor: 'pointer', fontSize: '8px', marginLeft: 'auto' }}>
                  Clear
                </button>
              </div>
              <input
                type="text"
                value={networkSearchQuery}
                onChange={(e) => setNetworkSearchQuery(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%',
                  padding: '2px 5px',
                  borderRadius: '2px',
                  border: '1px solid #334155',
                  background: '#0f172a',
                  color: '#e2e8f0',
                  fontSize: '9px',
                }}
              />
            </div>

            {/* Network requests list */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '4px 6px',
                minHeight: 0,
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
                touchAction: 'pan-y',
              }}
            >
              {filteredNetworkRequests.length === 0 ? (
                <div style={{ color: '#64748b', textAlign: 'center', padding: '12px', fontSize: '9px' }}>
                  No network requests found.
                </div>
              ) : (
                filteredNetworkRequests.map((req) => {
                  const isExpanded = expandedRequestId === req.id;
                  const isError = Boolean(req.error || (req.status && req.status >= 400));
                  const isSuccess = Boolean(req.status && req.status >= 200 && req.status < 400 && !req.error);

                  return (
                    <div key={req.id} style={{ marginBottom: '4px', border: '1px solid #334155', borderRadius: '3px', background: '#1e293b', overflow: 'hidden' }}>
                      {/* Request header - clickable */}
                      <div
                        onClick={() => setExpandedRequestId(isExpanded ? null : req.id)}
                        style={{
                          padding: '4px 6px',
                          cursor: 'pointer',
                          background: isExpanded ? '#0f172a' : 'transparent',
                          transition: 'background 0.2s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                          <span style={{ fontSize: '10px' }}>
                            {isError ? '❌' : isSuccess ? '✅' : '⏳'}
                          </span>
                          <span style={{ fontWeight: '700', fontSize: '8px', color: getMethodColor(req.method), background: '#0f172a', padding: '1px 4px', borderRadius: '2px' }}>
                            {req.method}
                          </span>
                          {req.status && (
                            <span style={{ fontSize: '8px', fontWeight: '600', color: getStatusColor(req.status) }}>
                              {req.status}
                            </span>
                          )}
                          {req.duration !== undefined && (
                            <span style={{ fontSize: '8px', color: '#94a3b8' }}>
                              {req.duration}ms
                            </span>
                          )}
                          <span style={{ fontSize: '8px', color: '#94a3b8', marginLeft: 'auto' }}>
                            {new Date(req.timestamp).toLocaleTimeString()}
                          </span>
                          <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                            {isExpanded ? '▼' : '▶'}
                          </span>
                        </div>
                        <div style={{ fontSize: '9px', color: '#e2e8f0', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                          {req.url}
                        </div>
                        {req.error && !isExpanded && (
                          <div style={{ marginTop: '2px', fontSize: '8px', color: '#f87171' }}>
                            Error: {req.error}
                          </div>
                        )}
                      </div>

                      {/* Expandable details */}
                      {isExpanded && (
                        <div style={{ padding: '4px 6px', background: '#0f172a', fontSize: '8px' }}>
                          {/* Request Headers */}
                          {req.requestHeaders && Object.keys(req.requestHeaders).length > 0 && (
                            <div style={{ marginBottom: '4px' }}>
                              <div style={{ color: '#3b82f6', fontWeight: '600', marginBottom: '2px', fontSize: '8px' }}>Request Headers:</div>
                              <div style={{ background: '#1e293b', padding: '4px', borderRadius: '2px', fontFamily: 'monospace', fontSize: '8px' }}>
                                {Object.entries(req.requestHeaders).map(([key, value]) => (
                                  <div key={key} style={{ marginBottom: '1px' }}>
                                    <span style={{ color: '#f59e0b' }}>{key}:</span> <span style={{ color: '#94a3b8' }}>{value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Request Body */}
                          {req.requestBody && (
                            <div style={{ marginBottom: '4px' }}>
                              <div style={{ color: '#3b82f6', fontWeight: '600', marginBottom: '2px', fontSize: '8px' }}>Request Body:</div>
                              <div style={{ background: '#1e293b', padding: '4px', borderRadius: '2px', fontFamily: 'monospace', fontSize: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                {typeof req.requestBody === 'string' ? req.requestBody : JSON.stringify(req.requestBody, null, 2)}
                              </div>
                            </div>
                          )}

                          {/* Response Headers */}
                          {req.responseHeaders && Object.keys(req.responseHeaders).length > 0 && (
                            <div style={{ marginBottom: '4px' }}>
                              <div style={{ color: '#10b981', fontWeight: '600', marginBottom: '2px', fontSize: '8px' }}>Response Headers:</div>
                              <div style={{ background: '#1e293b', padding: '4px', borderRadius: '2px', fontFamily: 'monospace', fontSize: '8px' }}>
                                {Object.entries(req.responseHeaders).map(([key, value]) => (
                                  <div key={key} style={{ marginBottom: '1px' }}>
                                    <span style={{ color: '#f59e0b' }}>{key}:</span> <span style={{ color: '#94a3b8' }}>{value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Response Body */}
                          {req.responseBody && (
                            <div style={{ marginBottom: '4px' }}>
                              <div style={{ color: '#10b981', fontWeight: '600', marginBottom: '2px', fontSize: '8px' }}>Response Body:</div>
                              <div style={{ background: '#1e293b', padding: '4px', borderRadius: '2px', fontFamily: 'monospace', fontSize: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '200px', overflowY: 'auto' }}>
                                {typeof req.responseBody === 'string' ? req.responseBody : JSON.stringify(req.responseBody, null, 2)}
                              </div>
                            </div>
                          )}

                          {/* Error Details */}
                          {req.error && (
                            <div style={{ marginBottom: '4px' }}>
                              <div style={{ color: '#ef4444', fontWeight: '600', marginBottom: '2px', fontSize: '8px' }}>Error:</div>
                              <div style={{ background: '#1e293b', padding: '4px', borderRadius: '2px', color: '#f87171', fontSize: '8px' }}>
                                {req.error}
                                {req.stack && (
                                  <pre style={{ marginTop: '4px', fontSize: '8px', whiteSpace: 'pre-wrap' }}>{req.stack}</pre>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );

      case 'storage':
        const localStorageItems = Object.entries(localStorage);
        const sessionStorageItems = Object.entries(sessionStorage);
        const cookies = document.cookie.split(';').filter(c => c.trim());
        const storageQuery = storageSearchQuery.trim().toLowerCase();

        const matchesQuery = (key: string, value: string) => {
          if (!storageQuery) return true;
          const content = `${key} ${value}`.toLowerCase();
          return content.includes(storageQuery);
        };

        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#0f172a', color: '#e2e8f0', minHeight: 0 }}>
            <div style={{ padding: '4px 6px', background: '#1e293b', display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {/* Clear Cache & Reload Button */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '8px', color: '#94a3b8' }}>
                  🔄 Clear cache & reload
                </span>
                <button
                  onClick={clearCacheAndReload}
                  style={{
                    padding: '2px 6px',
                    borderRadius: '2px',
                    border: 'none',
                    background: '#3b82f6',
                    color: 'white',
                    fontSize: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Clear Cache
                </button>
              </div>
              {/* Clear All Data Button */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '8px', color: '#94a3b8' }}>
                  Clear all data & reload
                </span>
                <button
                  onClick={clearAllData}
                  style={{
                    padding: '2px 6px',
                    borderRadius: '2px',
                    border: 'none',
                    background: '#ef4444',
                    color: 'white',
                    fontSize: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Clear All
                </button>
              </div>
              <input
                type="text"
                value={storageSearchQuery}
                onChange={(e) => setStorageSearchQuery(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%',
                  padding: '2px 5px',
                  borderRadius: '2px',
                  border: '1px solid #334155',
                  background: '#0f172a',
                  color: '#e2e8f0',
                  fontSize: '9px',
                }}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 6px', fontFamily: 'monospace', fontSize: '9px' }}>
              <div style={{ marginBottom: '4px' }}>
                <h4 style={{ margin: '0 0 2px 0', color: '#60a5fa', fontSize: '9px' }}>
                  📦 localStorage ({localStorageItems.length})
                </h4>
                {localStorageItems.length === 0 ? (
                  <div style={{ color: '#64748b', padding: '2px', fontSize: '8px' }}>Empty</div>
                ) : (
                  <div style={{ background: '#1e293b', borderRadius: '2px', padding: '3px' }}>
                    {localStorageItems.filter(([key, value]) => matchesQuery(key, value)).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '2px', paddingBottom: '2px' }}>
                        <button
                          type="button"
                          onClick={() => setSelectedStorageItem({ scope: 'localStorage', key, value })}
                          style={{
                            color: '#fbbf24',
                            fontSize: '8px',
                            fontWeight: '600',
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          {key}
                        </button>
                        <div style={{ color: '#94a3b8', fontSize: '8px', marginTop: '1px', wordBreak: 'break-all' }}>
                          {value.substring(0, 60)}{value.length > 60 && '...'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '4px' }}>
                <h4 style={{ margin: '0 0 2px 0', color: '#60a5fa', fontSize: '9px' }}>
                  💾 sessionStorage ({sessionStorageItems.length})
                </h4>
                {sessionStorageItems.length === 0 ? (
                  <div style={{ color: '#64748b', padding: '2px', fontSize: '8px' }}>Empty</div>
                ) : (
                  <div style={{ background: '#1e293b', borderRadius: '2px', padding: '3px' }}>
                    {sessionStorageItems.filter(([key, value]) => matchesQuery(key, value)).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '2px', paddingBottom: '2px' }}>
                        <button
                          type="button"
                          onClick={() => setSelectedStorageItem({ scope: 'sessionStorage', key, value })}
                          style={{
                            color: '#fbbf24',
                            fontSize: '8px',
                            fontWeight: '600',
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          {key}
                        </button>
                        <div style={{ color: '#94a3b8', fontSize: '8px', marginTop: '1px', wordBreak: 'break-all' }}>
                          {value.substring(0, 60)}{value.length > 60 && '...'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '4px' }}>
                <h4 style={{ margin: '0 0 2px 0', color: '#60a5fa', fontSize: '9px' }}>
                  🍪 Cookies ({cookies.length})
                </h4>
                {cookies.length === 0 ? (
                  <div style={{ color: '#64748b', padding: '2px', fontSize: '8px' }}>Empty</div>
                ) : (
                  <div style={{ background: '#1e293b', borderRadius: '2px', padding: '3px' }}>
                    {cookies.map((cookie, idx) => {
                      const [key, ...rest] = cookie.split('=');
                      const value = rest.join('=');
                      if (!matchesQuery(key.trim(), value)) {
                        return null;
                      }
                      return (
                        <div key={idx} style={{ marginBottom: '2px', paddingBottom: '2px' }}>
                          <button
                            type="button"
                            onClick={() => setSelectedStorageItem({ scope: 'cookies', key: key.trim(), value })}
                            style={{
                              color: '#fbbf24',
                              fontSize: '8px',
                              fontWeight: '600',
                              background: 'transparent',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            {key.trim()}
                          </button>
                          <div style={{ color: '#94a3b8', fontSize: '8px', marginTop: '1px', wordBreak: 'break-all' }}>
                            {value}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {selectedStorageItem && (
                <div style={{ marginBottom: '4px', border: '1px solid #334155', borderRadius: '3px', background: '#1e293b', padding: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                    <div style={{ fontSize: '8px', fontWeight: 600, color: '#e2e8f0' }}>
                      {selectedStorageItem.scope} ▸ {selectedStorageItem.key}
                    </div>
                    <div style={{ display: 'flex', gap: '2px' }}>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(selectedStorageItem.value);
                          } catch {
                            // Ignore clipboard errors
                          }
                        }}
                        style={{
                          padding: '2px 4px',
                          borderRadius: '2px',
                          border: '1px solid #334155',
                          background: '#0f172a',
                          color: '#e2e8f0',
                          fontSize: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        Copy
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedStorageItem(null)}
                        style={{
                          padding: '2px 4px',
                          borderRadius: '2px',
                          border: '1px solid #334155',
                          background: '#0f172a',
                          color: '#e2e8f0',
                          fontSize: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                  <div style={{ background: '#0f172a', borderRadius: '2px', padding: '4px', fontSize: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '150px', overflowY: 'auto' }}>
                    {(() => {
                      try {
                        return JSON.stringify(JSON.parse(selectedStorageItem.value), null, 2);
                      } catch {
                        return selectedStorageItem.value;
                      }
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!envEnabled && !forceVisible) {
    return null;
  }

  const errorCount = logs.filter(l => l.type === 'error').length + networkStats.errors;

  return (
    <>
      {/* Toggle Button — small notch at top center */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          position: 'fixed',
          top: 'env(safe-area-inset-top, 0px)',
          left: '50%',
          transform: 'translateX(-50%)',
          width: isOpen ? '72px' : (errorCount > 0 ? '36px' : '28px'),
          height: '16px',
          borderRadius: '0 0 8px 8px',
          border: 'none',
          background: isOpen
            ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%)'
            : errorCount > 0
              ? 'rgba(239, 68, 68, 0.85)'
              : 'rgba(15, 23, 42, 0.5)',
          color: 'white',
          fontSize: '9px',
          fontWeight: '700',
          cursor: 'pointer',
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2px',
          transition: 'all 0.3s ease',
          opacity: isOpen ? 1 : 0.8,
          padding: 0,
        }}
        title="Toggle Dev Console (Ctrl+Shift+K)"
      >
        {isOpen && <span style={{ fontSize: '8px', lineHeight: 1 }}>CLOSE</span>}
        {!isOpen && errorCount > 0 && (
          <span style={{ fontSize: '8px', lineHeight: 1 }}>{errorCount}</span>
        )}
      </button>

      {/* Console Panel */}
      {isOpen && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: isFullscreen ? '100vh' : '60vh', background: 'white', borderTop: '2px solid #e5e7eb', zIndex: 100000, display: 'flex', flexDirection: 'column', boxShadow: '0 -4px 12px rgba(0,0,0,0.1)', transition: 'height 0.3s ease' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', background: '#f9fafb', padding: '0 4px', gap: '2px', alignItems: 'center' }}>
            {(['console', 'network', 'storage'] as TabType[]).map(tab => {
              const errorCount = logs.filter(l => l.type === 'error').length;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '4px 8px',
                    border: 'none',
                    background: activeTab === tab ? '#0f172a' : 'transparent',
                    color: activeTab === tab ? '#e2e8f0' : '#6b7280',
                    cursor: 'pointer',
                    fontSize: '9px',
                    fontWeight: '600',
                    borderRadius: '3px 3px 0 0',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                  }}
                >
                  {tab === 'console' && (
                    <>
                      📋 Console
                      {errorCount > 0 && (
                        <span style={{
                          background: '#ef4444',
                          color: 'white',
                          borderRadius: '6px',
                          padding: '1px 4px',
                          fontSize: '8px',
                          fontWeight: '700',
                          minWidth: '14px',
                          textAlign: 'center'
                        }}>
                          {errorCount}
                        </span>
                      )}
                    </>
                  )}
                  {tab === 'network' && (
                    <>
                      🌐 Network
                      {networkStats.errors > 0 && (
                        <span style={{
                          background: '#ef4444',
                          color: 'white',
                          borderRadius: '6px',
                          padding: '1px 4px',
                          fontSize: '8px',
                          fontWeight: '700',
                          minWidth: '14px',
                          textAlign: 'center'
                        }}>
                          {networkStats.errors}
                        </span>
                      )}
                    </>
                  )}
                  {tab === 'storage' && '💾 Storage'}
                </button>
              );
            })}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '3px' }}>
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                style={{
                  padding: '2px 6px',
                  borderRadius: '2px',
                  border: '1px solid #d1d5db',
                  background: isFullscreen ? '#3b82f6' : 'white',
                  color: isFullscreen ? 'white' : 'inherit',
                  cursor: 'pointer',
                  fontSize: '9px',
                  transition: 'all 0.2s'
                }}
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                {isFullscreen ? '⤓' : '⤢'}
              </button>
              <button onClick={() => setIsOpen(false)} style={{ padding: '2px 6px', borderRadius: '2px', border: 'none', background: '#e5e7eb', cursor: 'pointer', fontSize: '9px' }}>
                ✕
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {renderTabContent()}
        </div>
      )}
    </>
  );
}
